import React, {useMemo, useState} from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Alert, alertClasses, Box, Button, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import PreventativeCorrectionDialog from "../../components/preventative-correction-dialog";
import {getImportFieldDisplayLabel} from "../../engine/shared/import-field-display-label";

const ReviewImportingSection = ({
                                    state,
                                    usableRecords,
                                    onPreventativeCorrectionSubmit,
                                    onUserSkippedRowIndicesChange,
                                    mappingFields = [],
                                    staticDataContext = {},
                                }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport'});
    const [dialogOpen, setDialogOpen] = useState(false);
    const hasPreventativeIssues = state.preventativeIssues?.length > 0;

    const fieldDisplayLabelContext = useMemo(() => ({
        mappingFields,
        allPriceLevels: staticDataContext.allPriceLevels ?? [],
        formatPriceLevelLabel: (title, levelIndex) => t('fieldDisplayLabel.priceLevelSuffix', {title, levelIndex}),
    }), [mappingFields, staticDataContext.allPriceLevels, t]);

    return (
        <div className={'review-importing-section'} style={{position: 'relative'}}>
            <Grid2 container spacing={2} justifyContent={'center'} className={'indicator-section mx-3'}>
                <Grid2 xs={'auto'}>
                    <Box className={'indicator-item'}>
                        <p className={'indicator-label'}>Data Source:</p>
                        <p className={'indicator-value'}>{state.selectedDataType.title}</p>
                    </Box>
                </Grid2>
                <Grid2 xs={'auto'}>
                    <Box className={'indicator-item'}>
                        <p className={'indicator-label'}>Imported Records Count:</p>
                        <p className={'indicator-value'}>{state.importedFile?.length ?? 0}</p>
                    </Box>
                </Grid2>
                <Grid2 xs={'auto'}>
                    <Box className={'indicator-item'}>
                        <p className={'indicator-label'}>API Callable Records Count:</p>
                        <p className={'indicator-value'}>{usableRecords}</p>
                    </Box>
                </Grid2>
                {
                    hasPreventativeIssues &&
                    <Grid2 xs={12}>
                        <Alert
                            severity="error"
                            variant="filled"
                            sx={{
                                maxWidth: 900,
                                marginInline: 'auto',
                                [`.${alertClasses.message}`]: {
                                    flexShrink: 1,
                                },
                                [`.${alertClasses.action}`]: {
                                    flexShrink: 0,
                                },
                            }}
                            action={
                                <Button
                                    color="primary"
                                    size="small"
                                    onClick={() => setDialogOpen(true)}
                                    sx={{fontWeight: 'bold', textTransform: 'none'}}
                                >
                                    Resolve Issues
                                </Button>
                            }
                        >
                            <Typography variant="subtitle2" sx={{fontWeight: 'bold'}}>
                                Preventative Data Issues Detected
                            </Typography>
                            <Typography variant="body2" sx={{opacity: 0.9}}>
                                {state.preventativeIssues.length} records have invalid data (bad part numbers, missing required fields, etc.).
                                You can review and correct these issues before importing to ensure data quality and prevent import failures.
                            </Typography>
                        </Alert>
                    </Grid2>
                }
            </Grid2>
            <Grid2 container spacing={2} sx={{width: '100%'}} className={'review-importing-section-data'}>
                <Grid2 xs={12}>
                    <Typography variant="subtitle1" sx={{fontWeight: 600, paddingInline: 2}}>
                        Column Mapping Summary:
                    </Typography>
                </Grid2>
                {
                    state.columnMapping && Object.entries(state.columnMapping).map(([fieldName, csvColumn]) => {
                        const field = mappingFields.find((f) => f.name === fieldName);
                        const label = getImportFieldDisplayLabel(field, fieldDisplayLabelContext) || fieldName;
                        return (
                            <Grid2 key={fieldName} xs={12} md={6}>
                                <Box className={'item'}>
                                    <p className={'label'}>{`${label}: `}</p>
                                    <div className={'value'}>
                                        <p>{csvColumn}</p>
                                    </div>
                                </Box>
                            </Grid2>
                        );
                    })
                }
            </Grid2>
            <PreventativeCorrectionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                parsedRows={state.parsedRows}
                preventativeIssues={state.preventativeIssues}
                selectedDataType={state.selectedDataType}
                mappingFields={mappingFields}
                staticDataContext={staticDataContext}
                userSkippedRowIndices={state.userSkippedRowIndices}
                onUserSkippedRowIndicesChange={onUserSkippedRowIndicesChange}
                userSkippedFieldCells={state.userSkippedFieldCells}
                onSubmit={({patchedRows, ignoredRowIndices, skippedFieldCells}) => {
                    onPreventativeCorrectionSubmit({patchedRows, ignoredRowIndices, skippedFieldCells});
                    setDialogOpen(false);
                }}
            />
        </div>
    );
}

export default ReviewImportingSection;
